// src/scenes/BestiaryScene.js
// ─────────────────────────────────────────────────────────────────────────────
// Single scrollable grid of all 30 creatures.
// Locked = full black silhouette + "???".  Unlocked = tappable detail popup.
// Receives { regionId } from MapScene to theme the background.
// ─────────────────────────────────────────────────────────────────────────────

import Phaser from "phaser";
import { BESTIARY, TYPE_META, TYPE_ORDER } from "../data/bestiaryData.js";
import { LevelStore } from "../game/levelStore.js";
import { RegionThemes, hexToPhaser } from "../data/regionThemes.js";
import { createBackground } from "../vfx/background.js";

// ── Pré-carrega todas as imagens das criaturas via Vite glob ──────────────────
// Produz um objecto { './path/name.png': urlString } com eager: true
const _creatureImgs = import.meta.glob(
  "../assets/monsters/creatures/**/*.png",
  { eager: true, import: "default" }
);
// Converte para { "cindrakeet": url, "blazion": url, ... }
const CREATURE_IMG = {};
for (const [path, url] of Object.entries(_creatureImgs)) {
  const name = path.split("/").pop().replace(".png", "");
  CREATURE_IMG[name] = url;
}

// ── Shared lookup tables ──────────────────────────────────────────────────────
const ICONS = {
  fire: "🔥", electric: "⚡", water: "💧", earth: "🌿", psychic: "🧠",
};

export default class BestiaryScene extends Phaser.Scene {
  constructor() { super("BestiaryScene"); }

  // ── preload: carrega sprites das criaturas ────────────────────────────────
  preload() {
    for (const creature of BESTIARY) {
      const url = CREATURE_IMG[creature.id];
      if (url) {
        this.load.image(`creature-${creature.id}`, url);
      }
    }
  }

  // ── create ───────────────────────────────────────────────────────────────────
  create(data) {
    const W = this.scale.width;
    const H = this.scale.height;

    // Background inherits the region that launched us
    const regionId = data?.regionId || "volcano";
    createBackground(this, regionId);

    // ── Layout constants ──────────────────────────────────────────────────────
    const HEADER_H = 72;
    const PAD      = 16;
    const COLS     = 3;
    const GAP      = 10;
    const CARD_W   = Math.floor((W - PAD * 2 - GAP * (COLS - 1)) / COLS);
    const CARD_H   = 130;
    const SEC_H    = 42;   // type-section header height
    const DP       = 10;   // base world depth for grid content
    const DH       = 100;  // header depth (always on top)

    const unlocked = LevelStore.getUnlockedCreatures();

    // ── Fixed header (scrollFactor 0 = stays on screen regardless of camera) ──
    const headerTheme = RegionThemes[regionId] || RegionThemes.volcano;
    const headerAcc   = hexToPhaser(headerTheme.primary);

    // Dark strip
    this.add
      .rectangle(W / 2, HEADER_H / 2, W, HEADER_H, 0x060b12, 0.97)
      .setScrollFactor(0).setDepth(DH);

    // Bottom accent line
    this.add
      .rectangle(W / 2, HEADER_H - 1, W, 2, headerAcc, 0.55)
      .setScrollFactor(0).setDepth(DH);

    // Back button (left)
    const backBtn = this.add
      .text(PAD, HEADER_H / 2, "← MAPA", {
        fontFamily: "system-ui, Arial", fontSize: "13px", color: "#64748b",
      })
      .setOrigin(0, 0.5).setScrollFactor(0).setDepth(DH + 1)
      .setInteractive({ useHandCursor: true });
    backBtn.on("pointerover",  () => backBtn.setColor("#e2e8f0"));
    backBtn.on("pointerout",   () => backBtn.setColor("#64748b"));
    backBtn.on("pointerdown",  () => this.scene.start("MapScene"));

    // Title (centred, two lines)
    this.add
      .text(W / 2, HEADER_H / 2 - 9, "✦  BESTIÁRIO", {
        fontFamily: "system-ui, Arial", fontSize: "17px",
        color: "#e2e8f0", fontStyle: "bold",
      })
      .setOrigin(0.5, 1).setScrollFactor(0).setDepth(DH + 1);

    this.add
      .text(W / 2, HEADER_H / 2 + 5, `${unlocked.length} de 30 descobertos`, {
        fontFamily: "system-ui, Arial", fontSize: "11px", color: "#475569",
      })
      .setOrigin(0.5, 0).setScrollFactor(0).setDepth(DH + 1);

    // ── Build scrollable grid (world-space objects) ───────────────────────────
    // Group creatures by type
    const byType = {};
    for (const type of TYPE_ORDER) {
      byType[type] = BESTIARY.filter(c => c.type === type);
    }

    let worldY = HEADER_H + 10; // start just below fixed header

    for (const type of TYPE_ORDER) {
      const meta    = TYPE_META[type];
      const theme   = RegionThemes[meta.region] || RegionThemes.volcano;
      const acc     = hexToPhaser(theme.primary);
      const crts    = byType[type];
      const unlockedCount = crts.filter(c => unlocked.includes(c.id)).length;

      // ── Section header ──────────────────────────────────────────────────────
      const secCy = worldY + SEC_H / 2;

      // Left accent strip
      this.add.rectangle(PAD, secCy, 3, SEC_H - 8, acc, 1).setDepth(DP + 1);

      // Type icon + label
      this.add.text(PAD + 10, secCy, `${meta.icon}  ${meta.label}`, {
        fontFamily: "system-ui, Arial", fontSize: "13px",
        color: theme.primary, fontStyle: "bold",
      }).setOrigin(0, 0.5).setDepth(DP + 1);

      // Unlock count (right-aligned)
      this.add.text(W - PAD, secCy, `${unlockedCount} / 6`, {
        fontFamily: "system-ui, Arial", fontSize: "11px", color: "#334155",
      }).setOrigin(1, 0.5).setDepth(DP + 1);

      // Separator line
      this.add
        .rectangle(W / 2, worldY + SEC_H - 2, W - PAD * 2, 1, 0x1e293b, 1)
        .setDepth(DP + 1);

      worldY += SEC_H + 6;

      // ── 6 cards (2 rows × 3 cols) ─────────────────────────────────────────
      for (let i = 0; i < crts.length; i++) {
        const col = i % COLS;
        const row = Math.floor(i / COLS);
        const cx  = PAD + col * (CARD_W + GAP) + CARD_W / 2;
        const cy  = worldY + row * (CARD_H + GAP) + CARD_H / 2;
        const isUnlocked = unlocked.includes(crts[i].id);
        this._drawCard(cx, cy, CARD_W, CARD_H, crts[i], isUnlocked, acc, theme, DP);
      }

      const rows = Math.ceil(crts.length / COLS);
      worldY += rows * (CARD_H + GAP) + 22; // gap after each type group
    }

    // ── Scroll: drag with inertia ─────────────────────────────────────────────
    const totalContentH = worldY + 24;
    const maxScroll     = Math.max(0, totalContentH - H);
    this._maxScroll  = maxScroll;
    this._scrollVel  = 0;
    this._hasMoved   = false;

    let _py = 0, _lastY = 0;

    this.input.on("pointerdown", p => {
      _py = p.y;
      _lastY = p.y;
      this._hasMoved = false;
      this._scrollVel = 0;
    });

    this.input.on("pointermove", p => {
      if (!p.isDown) return;
      const dy = _lastY - p.y;
      _lastY = p.y;
      if (Math.abs(p.y - _py) > 6) this._hasMoved = true;
      if (this._hasMoved) {
        this._scrollVel = dy;
        this.cameras.main.scrollY = Phaser.Math.Clamp(
          this.cameras.main.scrollY + dy, 0, maxScroll
        );
      }
    });
  }

  update() {
    if (Math.abs(this._scrollVel) > 0.5) {
      this._scrollVel *= 0.90;
      this.cameras.main.scrollY = Phaser.Math.Clamp(
        this.cameras.main.scrollY + this._scrollVel, 0, this._maxScroll
      );
    } else {
      this._scrollVel = 0;
    }
  }

  // ── _drawCard ────────────────────────────────────────────────────────────────
  _drawCard(cx, cy, cw, ch, creature, isUnlocked, accentInt, theme, D) {
    const CIRCLE_R = 37;
    const nameY    = cy + ch / 2 - 24; // name text top position

    if (isUnlocked) {
      // ── Unlocked card ────────────────────────────────────────────────────
      const bg = this.add
        .rectangle(cx, cy, cw, ch, 0x0d1527, 0.90)
        .setStrokeStyle(1, accentInt, 0.30)
        .setDepth(D + 1)
        .setInteractive({ useHandCursor: true });

      // Hover brightens the border
      bg.on("pointerover", () => bg.setStrokeStyle(1, accentInt, 0.85));
      bg.on("pointerout",  () => bg.setStrokeStyle(1, accentInt, 0.30));
      // Tap (not drag) opens detail
      bg.on("pointerup", () => {
        if (!this._hasMoved) this._showDetail(creature, theme, accentInt);
      });

      // Soft glow behind circle
      this.add.circle(cx, cy - 14, CIRCLE_R + 10, accentInt, 0.06).setDepth(D + 2);

      // Creature circle
      this.add
        .circle(cx, cy - 14, CIRCLE_R, accentInt, 0.15)
        .setStrokeStyle(2, accentInt, 0.85)
        .setDepth(D + 2);

      // Sprite da criatura (com fallback para emoji se imagem não existir)
      const spriteKey = `creature-${creature.id}`;
      if (this.textures.exists(spriteKey)) {
        this.add.image(cx, cy - 14, spriteKey)
          .setDisplaySize(CIRCLE_R * 1.7, CIRCLE_R * 1.7)
          .setDepth(D + 3);
      } else {
        this.add
          .text(cx, cy - 14, ICONS[creature.type] || "✦", { fontSize: "30px" })
          .setOrigin(0.5).setDepth(D + 3);
      }

      // Name (small, caps)
      this.add
        .text(cx, nameY, creature.name.toUpperCase(), {
          fontFamily: "system-ui, Arial", fontSize: "9px",
          color: "#c8d8f0", fontStyle: "bold", align: "center",
          wordWrap: { width: cw - 12 },
        })
        .setOrigin(0.5, 0).setDepth(D + 3);

    } else {
      // ── Locked card — full black silhouette ──────────────────────────────
      this.add
        .rectangle(cx, cy, cw, ch, 0x060a10, 0.96)
        .setStrokeStyle(1, 0x111c2e, 1)
        .setDepth(D + 1);

      // Black silhouette circle
      this.add
        .circle(cx, cy - 14, CIRCLE_R, 0x070707, 1)
        .setStrokeStyle(2, 0x121c2c, 1)
        .setDepth(D + 2);

      // Barely-visible "?" inside silhouette
      this.add
        .text(cx, cy - 14, "?", {
          fontFamily: "system-ui, Arial", fontSize: "26px",
          color: "#141e30", fontStyle: "bold",
        })
        .setOrigin(0.5).setDepth(D + 3);

      // "???" name placeholder
      this.add
        .text(cx, nameY, "???", {
          fontFamily: "system-ui, Arial", fontSize: "9px", color: "#141e30",
        })
        .setOrigin(0.5, 0).setDepth(D + 3);
    }
  }

  // ── _showDetail ───────────────────────────────────────────────────────────────
  // Fades in a bottom panel with the creature's full info.
  // Tap outside (on the scrim) to close.
  _showDetail(creature, theme, accentInt) {
    const W = this.scale.width;
    const H = this.scale.height;
    const SD = 200; // detail depth (above header at DH=100)

    const objs = [];
    const push    = o => { objs.push(o); return o; };
    const destroy = () => objs.forEach(o => { try { o?.destroy?.(); } catch (_) {} });

    // Scrim — tap to close
    const scrim = push(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.75)
        .setScrollFactor(0).setDepth(SD).setInteractive()
    );
    scrim.once("pointerdown", destroy);

    // ── Panel: gradient + border ──────────────────────────────────────────────
    const PANEL_H = 360;
    const panelY  = H - PANEL_H;
    const ac      = accentInt;

    // Derive subtle bottom tint from accent colour
    const botR = Math.min(255, 0x0a + Math.floor(((ac >> 16) & 0xff) * 0.12));
    const botG = Math.min(255, 0x0c + Math.floor(((ac >> 8)  & 0xff) * 0.12));
    const botB = Math.min(255, 0x14 + Math.floor(( ac        & 0xff) * 0.12));
    const botColor = (botR << 16) | (botG << 8) | botB;

    const panelGfx = push(this.add.graphics().setScrollFactor(0).setDepth(SD + 1));
    panelGfx.fillGradientStyle(0x080e18, 0x080e18, botColor, botColor, 1, 1, 1, 1);
    panelGfx.fillRect(0, panelY, W, PANEL_H);

    // Top accent line
    push(
      this.add
        .rectangle(W / 2, panelY, W, 2, accentInt, 0.70)
        .setScrollFactor(0).setDepth(SD + 2)
    );

    // Corner brackets (top-left ┌ and top-right ┐)
    const brGfx = push(this.add.graphics().setScrollFactor(0).setDepth(SD + 2));
    brGfx.lineStyle(2, accentInt, 1);
    const B = 18; // bracket arm length
    brGfx.lineBetween(0,     panelY, B,     panelY);      // ┌ horizontal
    brGfx.lineBetween(0,     panelY, 0,     panelY + B);  // ┌ vertical
    brGfx.lineBetween(W,     panelY, W - B, panelY);      // ┐ horizontal
    brGfx.lineBetween(W,     panelY, W,     panelY + B);  // ┐ vertical

    // ── Large creature image (falls back to emoji if texture missing) ────────
    push(
      this.add
        .circle(W / 2, panelY + 76, 56, accentInt, 0.14)
        .setStrokeStyle(3, accentInt, 0.90)
        .setScrollFactor(0).setDepth(SD + 2)
    );
    const spriteKey = `creature-${creature.id}`;
    if (this.textures.exists(spriteKey)) {
      push(
        this.add.image(W / 2, panelY + 76, spriteKey)
          .setDisplaySize(112, 112)
          .setScrollFactor(0).setDepth(SD + 3)
      );
    } else {
      push(
        this.add
          .text(W / 2, panelY + 76, ICONS[creature.type] || "✦", { fontSize: "46px" })
          .setOrigin(0.5).setScrollFactor(0).setDepth(SD + 3)
      );
    }

    // ── Name ─────────────────────────────────────────────────────────────────
    push(
      this.add
        .text(W / 2, panelY + 152, creature.name.toUpperCase(), {
          fontFamily: "system-ui, Arial", fontSize: "20px",
          color: theme.primary, fontStyle: "bold",
        })
        .setOrigin(0.5).setScrollFactor(0).setDepth(SD + 3)
    );

    // ── Type badge ────────────────────────────────────────────────────────────
    push(
      this.add
        .text(W / 2, panelY + 182,
          `${ICONS[creature.type]}  ${TYPE_META[creature.type].label}`, {
          fontFamily: "system-ui, Arial", fontSize: "11px", color: "#475569",
          backgroundColor: "#0d1320", padding: { x: 12, y: 5 },
        })
        .setOrigin(0.5).setScrollFactor(0).setDepth(SD + 3)
    );

    // ── Divider ───────────────────────────────────────────────────────────────
    const divGfx = push(this.add.graphics().setScrollFactor(0).setDepth(SD + 2));
    divGfx.lineStyle(1, accentInt, 0.22);
    divGfx.lineBetween(40, panelY + 215, W - 40, panelY + 215);

    // ── Description text ──────────────────────────────────────────────────────
    const descText = creature.text?.trim()
      ? creature.text
      : "— descrição em breve —";
    const descColor = creature.text?.trim() ? "#94a3b8" : "#1e293b";

    push(
      this.add
        .text(W / 2, panelY + 230, descText, {
          fontFamily: "system-ui, Arial", fontSize: "13px",
          color: descColor, align: "center", fontStyle: "italic",
          wordWrap: { width: W - 64 }, lineSpacing: 4,
        })
        .setOrigin(0.5, 0).setScrollFactor(0).setDepth(SD + 3)
    );

    // ── Tap-to-close hint ─────────────────────────────────────────────────────
    push(
      this.add
        .text(W / 2, H - 20, "toca fora para fechar", {
          fontFamily: "system-ui, Arial", fontSize: "11px", color: "#1e293b",
        })
        .setOrigin(0.5).setScrollFactor(0).setDepth(SD + 3)
    );

    // ── Fade-in all panel elements (skip scrim which appears instantly) ────────
    objs.slice(1).forEach(o => {
      if (o?.setAlpha) {
        o.setAlpha(0);
        this.tweens.add({ targets: o, alpha: 1, duration: 200, ease: "Quad.Out" });
      }
    });
  }
}
